<template>
  <div class="json-node">
    <div class="flex items-center space-x-2 py-1">
      <!-- Indent -->
      <div 
        class="flex-shrink-0" 
        :style="{ width: `${depth * 16}px` }"
      ></div>

      <!-- Collapse/Expand Button -->
      <button
        v-if="isExpandable"
        @click="toggle"
        class="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
      >
        <svg 
          class="w-3 h-3 transform transition-transform"
          :class="{ 'rotate-90': isExpanded }"
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
        </svg>
      </button>

      <!-- Spacer for non-expandable items -->
      <div v-else class="w-4"></div>

      <!-- Key -->
      <span 
        v-if="showKey"
        class="font-medium text-blue-900"
      >
        "{{ nodeKey }}":
      </span>

      <!-- Value -->
      <span class="flex-1">
        <!-- Primitive Values -->
        <span v-if="!isObject" :class="valueClasses">
          <span v-if="isString">"{{ data }}"</span>
          <span v-else-if="isNull">null</span>
          <span v-else>{{ data }}</span>
        </span>

        <!-- Object/Array Headers -->
        <span v-else class="text-gray-700">
          <span v-if="isArray">
            [ {{ data.length }} {{ data.length === 1 ? 'item' : 'items' }} ]
          </span>
          <span v-else>
            { {{ Object.keys(data).length }} {{ Object.keys(data).length === 1 ? 'property' : 'properties' }} }
          </span>
        </span>
      </span>

      <!-- Type Badge -->
      <span class="flex-shrink-0 text-xs text-gray-400 uppercase">
        {{ dataType }}
      </span>
    </div>

    <!-- Children -->
    <div v-if="isExpandable && isExpanded && isObject">
      <JsonNode
        v-for="(value, key) in data"
        :key="key"
        :data="value"
        :node-key="key"
        :path="[...path, key]"
        :depth="depth + 1"
        :expanded="expanded"
        @toggle="$emit('toggle', $event)"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  data: {
    required: true
  },
  nodeKey: {
    type: [String, Number],
    default: null
  },
  path: {
    type: Array,
    default: () => []
  },
  depth: {
    type: Number,
    default: 0
  },
  expanded: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['toggle'])

// Computed properties
const showKey = computed(() => props.nodeKey !== null)

const isObject = computed(() => {
  return typeof props.data === 'object' && props.data !== null
})

const isArray = computed(() => {
  return Array.isArray(props.data)
})

const isString = computed(() => {
  return typeof props.data === 'string'
})

const isNull = computed(() => {
  return props.data === null
})

const isExpandable = computed(() => {
  if (!isObject.value) return false
  if (isArray.value) return props.data.length > 0
  return Object.keys(props.data).length > 0
})

const pathKey = computed(() => props.path.join('.'))

const isExpanded = computed(() => {
  return props.expanded[pathKey.value] || false
})

const dataType = computed(() => {
  if (props.data === null) return 'null'
  if (isArray.value) return 'array'
  if (typeof props.data === 'object') return 'object'
  if (typeof props.data === 'string') return 'string'
  if (typeof props.data === 'number') return 'number'
  if (typeof props.data === 'boolean') return 'boolean'
  return 'unknown'
})

const valueClasses = computed(() => {
  const base = 'font-mono'
  
  switch (dataType.value) {
    case 'string':
      return `${base} text-green-700`
    case 'number':
      return `${base} text-purple-700`
    case 'boolean':
      return `${base} text-orange-700`
    case 'null':
      return `${base} text-gray-500 italic`
    default:
      return `${base} text-gray-700`
  }
})

// Methods
const toggle = () => {
  emit('toggle', props.path)
}
</script>

<style scoped>
.json-node {
  @apply text-sm;
}
</style>